import { CanceledError } from 'axios';
import { isEmpty } from 'lodash';

import { approvedStatusOptions } from '../../constants/approvedStatus';
import {
  PRICINGS_ERROR,
  PRICINGS_HIDDEN_COLUMNS,
  PRICINGS_LOADING,
  PRICINGS_PAGE,
  PRICINGS_ROWS_PER_PAGE,
  PRICINGS_SET_DATA,
  PRICINGS_SET_FILTER,
  PRICINGS_TOTAL_COUNT,
  RESET_PRICING_DATA,
} from '../reducers/pricings';
import { alertError, alertSuccess } from './alert';
import { exportPricing, getPricing, uploadPricingFile } from './api/pricing';
import { getTotalCountHeader } from './api/utils';
import { CSV_DELIMITER, CSV_MIME_TYPE, parseCsvFile, TSV_DELIMITER } from '../../utils/csv';
import { isResponseOk } from '../../utils/api';
import history from '../../utils/history';

export const PRICING_FILTER_SEARCH_KEY = 'search';
export const PRICING_FILTER_STATUSES_KEY = 'statuses';
export const PRICING_FILTER_HAS_PRICE_KEY = 'hasPrice';
export const PRICING_FILTER_CURRENCIES_KEY = 'currencies';
export const PRICING_FILTER_CHANNELS_KEY = 'channels';

const setPricingData = (data) => ({ type: PRICINGS_SET_DATA, payload: data });
const setPricingLoading = { type: PRICINGS_LOADING };
const setPricingTotalCount = (count) => ({ type: PRICINGS_TOTAL_COUNT, payload: count });
const setPricingError = { type: PRICINGS_ERROR, payload: true };

export const setPricingPage = (page) => ({ type: PRICINGS_PAGE, payload: page });
export const setPricingRowsPerPage = (rowsPerPage) => ({ type: PRICINGS_ROWS_PER_PAGE, payload: rowsPerPage });
export const setPricingHiddenColumns = (columns) => ({ type: PRICINGS_HIDDEN_COLUMNS, payload: columns });
export const setPricingFilter = (data) => ({ type: PRICINGS_SET_FILTER, payload: { data } });
export const resetPricingData = () => ({ type: RESET_PRICING_DATA });

export const getSupplierRetailerPricing =
  (retailerId, options = {}) =>
  async (dispatch, getState) => {
    dispatch(setPricingLoading);
    try {
      const state = getState();
      const { filters, table } = state.pricings;
      const { supplierId } = state.role;
      const { pricing_currencies: currencies } = state.retailer.current?.retailer || {};

      const availableFilters = {
        ...getAvailablePricingReportFilters({ filters, currencies }),
        retailer_id: retailerId,
      };

      const response = await getPricing(supplierId, {
        limit: table.rowsPerPage,
        page: table.page,
        filters: availableFilters,
        ...options,
      });

      if (!isResponseOk(response)) {
        throw new Error(`Error getting pricing for retailer ${retailerId}`);
      }

      const totalCount = getTotalCountHeader(response);
      dispatch(setPricingTotalCount(totalCount));
      dispatch(setPricingData(response.data));
    } catch (error) {
      if (!(error instanceof CanceledError)) {
        dispatch(alertError(error));
        dispatch(setPricingError);
      }
    }
  };

export const getSupplierPricing =
  (options = {}) =>
  async (dispatch, getState) => {
    dispatch(setPricingLoading);
    try {
      const state = getState();
      const { filters, table } = state.pricings;
      const { supplierId } = state.role;

      const currencies = new Set();
      const supplierRetailers = state.role.supplier.supplier_retailers ?? [];
      supplierRetailers.forEach((supplierRetailer) => {
        supplierRetailer.retailer.currencies.forEach((currency) => currencies.add(currency.currency));
      });

      options.limit = table.rowsPerPage;
      options.page = table.page;

      const availableFilters = getAvailablePricingReportFilters({
        filters,
        currencies: Array.from(currencies),
        channels: supplierRetailers,
      });
      if (!isEmpty(availableFilters)) {
        options.filters = availableFilters;
      }

      const response = await getPricing(supplierId, options);

      if (!isResponseOk(response)) {
        throw new Error(`Error getting pricing for supplier ${supplierId}`);
      }

      const totalCount = getTotalCountHeader(response);
      dispatch(setPricingTotalCount(totalCount));
      dispatch(setPricingData(response.data));
    } catch (error) {
      if (!(error instanceof CanceledError)) {
        dispatch(alertError(error));
        dispatch(setPricingError);
      }
    }
  };

export function getAvailablePricingReportFilters({ filters, currencies, channels }) {
  return Object.keys(filters || {}).reduce((availableFilters, key) => {
    switch (key) {
      case PRICING_FILTER_CURRENCIES_KEY:
        if (currencies && filters.currencies.length < currencies.length) {
          availableFilters[PRICING_FILTER_CURRENCIES_KEY] = filters.currencies;
        }
        break;

      case PRICING_FILTER_STATUSES_KEY:
        if (filters.statuses.length < approvedStatusOptions.length) {
          availableFilters[PRICING_FILTER_STATUSES_KEY] = filters.statuses;
        }
        break;

      case PRICING_FILTER_HAS_PRICE_KEY:
        if (filters.hasPrice) {
          availableFilters.has_price = true;
        }
        break;
      case PRICING_FILTER_CHANNELS_KEY:
        if (channels && filters.channels.length < channels.length) {
          availableFilters[PRICING_FILTER_CHANNELS_KEY] = filters.channels;
        }
        break;
      default:
        if (filters[key].length > 0) {
          availableFilters[key] = filters[key];
        }
        break;
    }
    return availableFilters;
  }, {});
}

export const exportSupplierPricing = (options, addCurrentFilters) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { supplierId } = state.role;

    if (addCurrentFilters) {
      const { filters } = state.pricings;
      const currencies = getSupplierAvailableCurrencies(state.role.supplier);
      const channels = state.role.supplier.supplier_retailers ?? [];
      const availableFilters = getAvailablePricingReportFilters({
        filters,
        currencies,
        channels,
      });
      if (!isEmpty(availableFilters)) {
        Object.assign(options, availableFilters);
      }
    }

    const response = await exportPricing(supplierId, options);

    if (!isResponseOk(response)) {
      throw new Error(`Error exporting pricing for supplier ${supplierId}`);
    }

    dispatch(
      alertSuccess(
        [
          `Your pricing report is being generated and will be emailed to you within 30 minutes.
        It will also be available on the`,
          <button key="reports-page-link" onClick={() => history.push(`/suppliers/${supplierId}/analytics/reports`)}>
            Reports page
          </button>,
        ],
        false,
      ),
    );
  } catch (e) {
    dispatch(alertError(e));
  }
};

export const exportSupplierRetailerPricing = (options, addCurrentFilters) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { supplierId } = state.role;
    const { filters } = state.pricings;
    const { pricing_currencies: currencies, id } = state.retailer.current?.retailer || {};

    options.retailer_id = id;

    if (addCurrentFilters) {
      const availableFilters = getAvailablePricingReportFilters({ filters, currencies });
      if (!isEmpty(availableFilters)) {
        Object.assign(options, availableFilters);
      }
    }

    const response = await exportPricing(supplierId, options);

    if (!isResponseOk(response)) {
      throw new Error(`Error exporting pricing for supplier ${supplierId}`);
    }

    dispatch(alertSuccess(response.data?.message));
  } catch (e) {
    dispatch(alertError(e));
  }
};

export const uploadSupplierRetailerPricing = (file, retailerId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { supplierId } = state.role;
    const delimiter = file.type === CSV_MIME_TYPE ? CSV_DELIMITER : TSV_DELIMITER;
    const data = await parseCsvFile(file, delimiter).catch((errors) => {
      dispatch(alertError({ message: 'Error parsing file' }));
      console.error(errors);
    });

    if (!data) {
      throw new Error(`Couldn't parse file`);
    }
    const payload = {
      data,
      file_type: file.type === CSV_MIME_TYPE ? 'csv' : 'tsv',
    };

    if (retailerId) {
      payload.retailer_id = retailerId;
    }

    const response = await uploadPricingFile(supplierId, payload);
    if (!response || !isResponseOk(response)) {
      throw new Error(`Error uploading pricing for supplier ${supplierId}`);
    }

    dispatch(alertSuccess('file is being processed, a summary email will be sent'));
  } catch (e) {
    dispatch(alertError(e));
  }
};

export const getSupplierAvailableCurrencies = (supplier) => {
  const currencies = new Set();
  const supplierRetailers = supplier.supplier_retailers ?? [];
  supplierRetailers.forEach((supplierRetailer) => {
    supplierRetailer.retailer.currencies.forEach((currency) => currencies.add(currency.currency));
  });
  return Array.from(currencies);
};
